import { request } from "@/lib/api";

// Loose KPI bag — connector payloads are source-specific.
export type Kpis = Record<string, unknown>;

export type WindowMeta = {
  from: string;
  to: string;
  days: number;
  mode: "live" | "snapshot";
};

export type DeltaSeries = {
  key: string;
  label: string;
  source: string;
  current: number | null;
  previous: number | null;
  abs: number | null;
  pct: number | null;
};

export type ExecCard = {
  key: string;
  label: string;
  value: number | null;
  unit: "EUR" | "count" | string;
  source: string;
  tone?: "danger";
  delta?: { current: number | null; previous: number | null; abs: number | null; pct: number | null };
};

export type ExecutiveResponse = {
  cards: ExecCard[];
  branch_notes: string[];
  sources: Record<string, { status: string; captured_at: string | null }>;
  window: WindowMeta;
  alerts_summary: Record<string, number>;
  sales?: SalesResponse;
};

export type SourceHealthItem = {
  code: string;
  name: string;
  category: string;
  transport: string;
  configured: boolean;
  status: string;
  captured_at: string | null;
  age_seconds: number | null;
  rows: number;
  gaps: string[];
  last_run_status: string | null;
  last_run_at: string | null;
  last_run_duration_ms: number | null;
  last_error: string | null;
  last_success_age_seconds?: number | null;
  failure_kind?: string | null;
  network?: {
    required_network: string;
    docker_available: boolean;
    attached: boolean;
    missing_containers: string[];
    network_exists: boolean | null;
  } | null;
  recommended_fix?: string | null;
};

export type SourceHealthResponse = {
  sources: SourceHealthItem[];
  summary: Record<string, number>;
};

export type AlertItem = {
  id: number;
  rule: string;
  source: string | null;
  severity: "critical" | "warning" | "info";
  title: string;
  detail: string | null;
  first_seen_at: string;
  last_seen_at: string;
};

export type FailuresResponse = {
  alerts: AlertItem[];
  counts: Record<string, number>;
  total: number;
};

export type TrendsResponse = { series: DeltaSeries[]; window: WindowMeta };

export type LeaderboardRow = { name: string; value: number; count?: number; orders?: number };
export type Leaderboard = { key: string; label: string; source: string; unit: string; rows: LeaderboardRow[] };
export type LeaderboardsResponse = { boards: Leaderboard[]; window: WindowMeta };

export type ActivityItem = {
  source: string;
  kind: string;
  title: string;
  subtitle?: string | null;
  value?: number | null;
  unit?: string;
  status?: string | null;
  at?: string | null;
};
export type RecentActivityResponse = { items: ActivityItem[] };

export type SourceDetail = {
  code: string;
  name: string;
  category: string;
  status: string;
  captured_at: string | null;
  kpis: Kpis;
  gaps: string[];
  window: WindowMeta;
};

export type OdooCompanySales = {
  company_id: number;
  name: string;
  sales_incl: number;
  sales_excl: number;
  tax: number;
  invoices: number;
  unpaid: number;
};

export type SalesSource = {
  key: string;
  label: string;
  incl: number;
  excl: number;
  tax: number;
  count: number;
  unpaid: number;
  status: string;
  captured_at: string | null;
  available: boolean;
};

export type SalesResponse = {
  totals: {
    business_incl: number;
    business_excl: number;
    business_tax: number;
    unpaid: number;
    currency: string;
    basis: string;
  };
  sources: SalesSource[];
  odoo_companies: OdooCompanySales[];
  repairs: {
    incl: number;
    excl: number;
    tax: number;
    jobs: number;
    status: string;
    captured_at: string | null;
    branch_split: { name: string; jobs: number; excl: number; tax: number; incl: number }[];
    note: string;
  } | null;
  warnings: string[];
  window?: WindowMeta;
  branch_notes?: string[];
};

export type Insight = {
  code: string;
  title: string;
  detail: string;
  severity: "critical" | "warning" | "positive" | "info";
  direction: "up" | "down" | "flat";
  metric: number | null;
  change_pct: number | null;
  source: string | null;
};
export type InsightsResponse = { insights: Insight[]; counts: Record<string, number> };

export type LeagueRow = {
  rank: number;
  name: string;
  value: number;
  previous_rank: number | null;
  movement: number | null;
  trend_pct: number | null;
};
export type LeagueBoard = { key: string; label: string; unit: string; source: string; rows: LeagueRow[] };
export type StaffBoard = { key: string; label: string; unit: string; source: string; rows: { name: string; value: number; count?: number }[] };
export type LeaguesResponse = {
  branch: { boards: LeagueBoard[]; lookback_note: string };
  staff: { boards: StaffBoard[]; gaps: string[] };
};

export type AttentionItem = {
  rank: number;
  severity: "critical" | "warning" | "info";
  title: string;
  detail: string | null;
  source: string | null;
  kind: string;
  rule: string | null;
};
export type AttentionResponse = { items: AttentionItem[]; counts: Record<string, number>; total: number };

export type Target = {
  key: string;
  label: string;
  actual: number;
  target: number;
  unit: string;
  variance: number;
  pct_achieved: number | null;
  on_track: boolean;
  lower_better: boolean;
  source: string | null;
};
export type TargetsResponse = { targets: Target[]; note: string };

export type ForecastSeries = {
  key: string;
  label: string;
  current: number;
  previous: number;
  projected_next: number;
  growth_pct: number | null;
  unit: string;
  source: string;
  confidence: string;
};
export type ForecastResponse = { series: ForecastSeries[]; method: string };

export type DailyDay = {
  key: string;
  label: string;
  date: string;
  sales: number | null;
  odoo_sales: number | null;
  ecommerce_sales: number | null;
  repairs_completed: number | null;
  repairs_new: number | null;
  support_new: number | null;
  subscriptions_new: number | null;
};
export type DailyDelta = { value: number | null; pct: number | null };
export type DailyMetric = { today: number | null; vs_yesterday: DailyDelta; vs_last_week: DailyDelta; vs_last_month: DailyDelta };
export type DailySummaryResponse = { days: DailyDay[]; metrics: Record<string, DailyMetric>; as_of: string };

export type TrendCompare = { current: number | null; previous: number | null; change_pct: number | null };
export type KpiTrends = { kpi: string; label: string; unit: string; wow: TrendCompare; mom: TrendCompare; qoq: TrendCompare; yoy: TrendCompare };
export type SeriesPoint = { date: string; value: number };
export type SeriesResponse = { kpi: string; days: number; points: SeriesPoint[] };
export type CompareEntitiesResponse = {
  kpi: string; dim: string; unit: string;
  rows: { name: string; current: number; previous: number; change_pct: number | null }[];
};
export type ForecastKpi = {
  kpi: string; label: string; unit: string; points: number; horizon_days: number;
  projected: number | null; slope_per_day: number | null; confidence: string; method: string; recent_total?: number;
};
export type DimensionsResponse = {
  kpis: { kpi: string; label: string; unit: string; kind: string }[];
  companies: string[]; branches: string[];
};
export type LeaderHistoryResponse = {
  board: string;
  entities: { entity: string; history: { date: string; value: number; rank: number }[]; current_rank: number; first_rank: number; rank_movement: number; current_value: number }[];
};
export type ReportMetric = { kpi: string; label: string; unit: string; current: number | null; previous: number | null; change_pct: number | null };
export type BoardReportResponse = {
  period_days: number;
  sections: { key: string; title: string; metrics: ReportMetric[]; has_data: boolean }[];
  risks: { severity: string; title: string; detail: string | null; source: string | null }[];
  risk_counts: Record<string, number>;
  note: string;
};
export type WarehouseStatus = { earliest: string | null; latest: string | null; rows: number; distinct_kpis: number; leader_rows: number };

export type ReportMeta = {
  id: number; kind: string; title: string;
  period_start: string | null; period_end: string | null;
  generated_at: string; status: string; trigger: string;
};
export type ReportSchedule = {
  code: string; kind: string; cadence: string; hour: number;
  weekday: number | null; monthday: number | null; enabled: boolean;
  last_run_at: string | null; next_run_at: string | null;
};
export type ReportSection =
  | { kind: "metrics"; title: string; rows: { label: string; value: number | string | null; unit?: string; change_pct?: number | null }[] }
  | { kind: "table"; title: string; columns: string[]; rows: (string | number | null)[][] }
  | { kind: "list"; title: string; items: { severity?: string; title: string; detail?: string | null }[] };
export type ReportPayload = {
  kind: string; title: string; generated_at: string; period_start: string; period_end: string;
  sections: ReportSection[]; summary: string;
};
export type ReportDetail = ReportMeta & { payload: ReportPayload | null; summary: string | null };
export type NotificationChannelItem = { id: number; name: string; type: string; enabled: boolean; config_summary?: Record<string, unknown> };
export type SubscriptionItem = { id: number; channel_id: number; target: string; report_kind: string | null; min_severity: string | null; mode: string; enabled: boolean };
export type DeliveryItem = {
  id: number; created_at: string; channel_id: number | null; channel_type: string; target: string;
  subject: string; status: string; detail: string | null; ref_kind: string | null; ref_id: string | null;
  dedupe_key: string | null; attempts: number; last_attempt_at: string | null; delivered_at: string | null;
};
export type TargetHistoryItem = { period_end: string; actual: number; achieved: boolean };
export type TargetReportItem = Target & { history: TargetHistoryItem[]; missed_count: number; achieved_count: number };
export type DigestResponse = {
  latest_report: ReportMeta | null;
  next_report: { code: string; kind: string; next_run_at: string } | null;
  critical_alerts: number;
  open_alerts: number;
  recent_deliveries: { created_at: string; channel_type: string; target: string; status: string; subject: string }[];
};

// --- Executive Decision Centre ---
export type Priority = {
  key: string; category: string; severity: string; title: string; reason: string;
  source: string | null; entity_ref: string | null;
  impact_value: number; customer_value: number; age_hours: number;
  first_seen: string | null; origin_kind: string; score: number;
  action: { id: number; status: string; owner: string | null } | null;
};
export type PrioritiesResponse = {
  generated_at: string;
  priorities: Priority[];
  counts: { critical: number; warning: number; info: number; total: number; tracked: number };
};
export type WhatChangedMetric = {
  kpi: string; label: string; unit: string;
  current: number | null; previous: number | null; change_pct: number | null;
};
export type WhatChangedResponse = {
  since: string; have_history: boolean; metrics: WhatChangedMetric[];
  new_alerts: { rule_code: string; severity: string; title: string; source: string | null; at: string | null }[];
  resolved_alerts: { rule_code: string; title: string; source: string | null; at: string | null }[];
};
export type RiskCategory = {
  category: string; label: string; count: number; max_severity: string | null; items: Priority[];
};
export type RiskRegisterResponse = { generated_at: string; categories: RiskCategory[]; total: number };
export type ManagementAction = {
  id: number; dedup_key: string; category: string; severity: string; priority: string; title: string;
  detail: string | null; source_code: string | null; entity_ref: string | null;
  company: string | null; branch: string | null;
  status: string; owner: string | null; backup_owner: string | null;
  due_date: string | null; next_review_date: string | null; completed_date: string | null;
  overdue_days: number | null; is_overdue: boolean; time_in_status_hours: number | null;
  issue_statement: string | null; planned_action: string | null; success_criteria: string | null;
  completion_evidence: string | null; blocker_reason: string | null; decision_required: string | null;
  notes: string | null; origin: string; reopen_count: number; last_reopened_at: string | null;
  created_at: string | null; updated_at: string | null; resolved_at: string | null;
};
export type ActionLink = {
  id: number; action_id: number; link_type: string; link_ref: string; label: string | null; created_at: string | null;
};
export type AccountabilityOverview = {
  generated_at: string;
  counts: {
    active: number; overdue: number; due_today: number; due_this_week: number;
    unassigned: number; unassigned_high: number; blocked: number; needs_decision: number;
    recently_resolved: number; recently_dismissed: number;
  };
  by_owner: { owner: string; count: number }[];
  by_category: { category: string; count: number }[];
  by_priority: { priority: string; count: number }[];
  by_status: { status: string; count: number }[];
  overdue: ManagementAction[]; due_today: ManagementAction[]; unassigned: ManagementAction[];
  blocked: ManagementAction[]; needs_decision: ManagementAction[];
  recently_resolved: ManagementAction[]; recently_dismissed: ManagementAction[];
};
export type AccountabilityMetrics = {
  generated_at: string; window_days: number; resolved_total: number;
  resolved_on_time: number; resolved_late: number; resolved_due_unknown: number;
  active_overdue: number; reopened_total: number; avg_resolution_days: number | null;
  adequate_data: boolean; note: string | null;
};
export type ActionTemplate = {
  key: string; title: string; category: string; priority: string;
  due_in_days: number; success_criteria: string; suggested_link_type: string;
};
export type ActionUpdateBody = {
  status?: string; owner?: string; backup_owner?: string; due_date?: string | null; clear_due?: boolean;
  next_review_date?: string | null; clear_review?: boolean; priority?: string; company?: string; branch?: string;
  issue_statement?: string; planned_action?: string; success_criteria?: string; completion_evidence?: string;
  blocker_reason?: string; decision_required?: string; note?: string;
};
// Scenario planning (Phase 15)
export type Scenario = {
  id: number; title: string; description: string | null; scenario_type: string; status: string;
  horizon: string; period_start: string | null; period_end: string | null;
  company: string | null; branch: string | null; source_code: string | null;
  base_captured_at: string | null; base_trust: string | null; owner: string | null;
  created_at: string | null; updated_at: string | null;
};
export type ScenarioAssumption = {
  id: number; key: string; assumption_type: string; label: string; unit: string | null;
  baseline_value: number | null; proposed_value: number | null; bool_value: boolean | null;
  min_range: number | null; max_range: number | null; is_manual: boolean;
  confidence: string | null; explanation: string | null;
};
export type ScenarioLink = { id: number; link_type: string; link_ref: string; label: string | null; created_at: string | null };
export type ScenarioFull = { scenario: Scenario; assumptions: ScenarioAssumption[]; links: ScenarioLink[] };
export type ScenarioOutputs = {
  scenario: Scenario; outputs: Record<string, unknown>; manual_assumptions: string[];
  base_trust: string | null; caveats: string[];
};
export type ScenarioSummary = {
  active_count: number; unowned_count: number; stale_input_count: number;
  needs_decision: { id: number; title: string }[];
  biggest_sales_variance: { id: number; title: string; variance: number } | null;
};
export type ScenarioTemplate = { scenario_type: string; assumptions: Partial<ScenarioAssumption>[] };
export type ScenarioCompare = {
  baseline_actual: Record<string, unknown>; scenarios: ScenarioOutputs[]; labels: string[];
};
// Decision Records + Outcomes (Phase 16)
export type DecisionRecord = {
  id: number; title: string; description: string | null; category: string; status: string;
  company: string | null; branch: string | null; source_code: string | null;
  decision_date: string | null; target_review_date: string | null; actual_review_date: string | null;
  review_overdue: boolean; rationale: string | null; expected_outcome: string | null;
  constraints: string | null; risks_accepted: string | null; key_assumptions: string | null;
  review_note: string | null; cancel_reason: string | null; owner: string | null; backup_owner: string | null;
  superseded_by: number | null; approved_at: string | null; created_at: string | null; updated_at: string | null;
};
export type DecisionLink = { id: number; link_type: string; link_ref: string; label: string | null; created_at: string | null };
export type DecisionFull = { decision: DecisionRecord; links: DecisionLink[] };
export type DecisionSummary = {
  active_count: number; awaiting_review: number; review_overdue: number; proposed: number; approved_unowned: number;
  awaiting_review_list: { id: number; title: string }[];
  overdue_list: { id: number; title: string; due: string | null }[];
};
export type OutcomeComparison = {
  scenario_id: number; scenario_type: string; scope_status: string | null; maturity: string;
  accuracy_label: string | null; expected: Record<string, unknown>; actual: Record<string, unknown>;
  variance: number | null; variance_pct: number | null; formula: string | null; caveats: string[];
};
export type ScenarioQualityRow = {
  id: number; title: string; scenario_type: string; status: string; owner: string | null;
  scope_status: string | null; scope_note: string | null; base_captured_at: string | null; trust: string;
  assumption_count: number; manual_assumption_count: number; maturity: string | null; accuracy_label: string | null;
};
// Decision Portfolio (Phase 17)
export type PortfolioSummary = {
  generated_at: string;
  counts: Record<string, number>;
  governance_completeness: Record<string, number>;
  action_coverage_pct: number | null;
  metrics: { adequate_review_sample: boolean; review_on_time_rate_pct?: number; reviewed_count: number; note?: string };
  review_overdue: DecisionRecord[]; approved_no_action: DecisionRecord[]; awaiting_review: DecisionRecord[];
  blocked_action: DecisionRecord[]; recently_reviewed: DecisionRecord[]; accepted_risks_expiring: AcceptedRisk[];
  no_causation: string;
};
export type AgendaItem = {
  kind: string; decision_id: number; title: string; category: string; status: string; owner: string | null;
  why: string; severity: string; review_due: string | null; caveat: string | null; link: string;
  scenario_id?: number; scope_status?: string; risk_id?: number; risk_severity?: string; risk_status?: string;
};
export type AgendaResult = {
  generated_at: string; sections: { key: string; title: string; items: AgendaItem[]; empty: boolean }[];
  discussion_order: { decision_id: number; title: string; why: string; severity: string; link: string }[];
  item_count: number; section_count: number; no_recommendation: string; no_causation: string;
  cadence?: Cadence; preview?: boolean;
};
export type EffectivenessResult = {
  eligible: number; min_sample?: number; sample_status?: string; note?: string; no_causation: string; no_ranking?: string;
  label_counts?: Record<string, number>;
  groups?: { by_category?: Record<string, GroupRow>; by_scenario_type?: Record<string, GroupRow>;
             by_scope_status?: Record<string, GroupRow>; by_accuracy_label?: Record<string, number> };
  distribution?: Record<string, number>;
};
export type GroupRow = { count: number; labels: Record<string, number>; sample_status: string; broadly_aligned_pct?: number };
export type Cadence = {
  id: number; title: string; cadence_type: string; enabled: boolean; owner: string | null; backup_owner: string | null;
  target_weekday: number | null; target_monthday: number | null; timezone: string;
  next_review_at: string | null; last_generated_at: string | null; categories: string[];
  company: string | null; branch: string | null; source_code: string | null; min_severity: string | null;
  include_overdue_actions: boolean; include_blocked_decisions: boolean; include_material_variance: boolean;
  include_stale_scenario: boolean; channel_ref: string | null; created_at: string | null; updated_at: string | null;
};
export type AcceptedRisk = {
  id: number; decision_id: number; title: string; rationale: string | null; severity: string;
  status: string; effective_status: string; approved_by_email: string | null; accepted_at: string | null;
  review_date: string | null; expiry_date: string | null; withdrawn_reason: string | null;
};
export type GovernanceResult = {
  decision: DecisionRecord; completeness: Record<string, boolean | null>;
  accepted_risks: AcceptedRisk[]; links: DecisionLink[];
};
// Historical durability (Phase 18)
export type CoverageSource = {
  source: string; label: string; configured: boolean; connector_status: string; connector_failing: boolean;
  earliest_date: string | null; latest_date: string | null; expected_days: number; available_days: number;
  missing_days: number; continuity_pct: number | null; zero_value_days: number; introduced_late: boolean;
  recent_only: boolean; status: string; trust: string; last_success_at: string | null;
  ok_runs: number; failed_runs: number; caveats: string[];
};
export type CoverageSummary = {
  generated_at: string; global_earliest: string | null;
  counts: { sources: number; adequate: number; partial: number; gaps: number; connectors_failing: number };
  connectors_failing: string[];
  comparison_capability: { mom_ready_domains: string[]; qoq_ready_domains: string[]; yoy_ready_domains: string[]; note: string };
  no_fabrication: string; sources: CoverageSource[];
  readiness: Record<string, { label: string; earliest_date: string | null;
    mtd: WinReadiness; mom: WinReadiness; qoq: WinReadiness; yoy: WinReadiness }>;
  non_kpi: Record<string, unknown>;
};
export type WinReadiness = { label: string; available: boolean; reason?: string; prior_window_continuity_pct?: number };
// Operational readiness (Phase 23)
export type OpsBackupAutomation = {
  automated: boolean; status: string; blocker: string | null; manual_checklist: string[]; safety: string;
  integrity_status?: string | null; schedule_hint?: string | null; next_scheduled_at?: string | null; runner?: string;
};
export type OpsReadiness = {
  generated_at: string; schema_version: string | null; app_commit: string; healthy: boolean;
  worker: { status: string; last_activity: string | null; minutes_since?: number; detail: string };
  storage: { db_size_mb: number; warnings: { severity: string; detail: string }[]; growth_trend: Record<string, unknown> };
  backup_automation: OpsBackupAutomation;
  backup: {
    last_backup_at: string | null; fresh: boolean | null; location_class: string; age_seconds: number | null;
    classification?: string; restore_classification?: string; off_host_protection?: boolean;
    automated?: boolean; next_scheduled_at?: string | null;
    last_restore_drill: { status: string; started_at?: string | null; schema_version?: string | null } | null;
  } | { unavailable: true };
  off_host?: { off_host_class: string; provisioned: boolean; verified: boolean; encryption_class: string; next_scheduled_at?: string | null } | { unavailable: true };
  dr?: { dr_status: string; dr_ready: boolean; rpo_local_met: boolean; rpo_off_host_met: boolean; rto_evidence_sufficient: boolean } | { unavailable: true };
  connector_slos: Record<string, number> | { unavailable: true };
  period_close: { latest_finalised_period_end: string | null; latest_finalised_type: string | null } | { unavailable: true };
  retention: { any_deletion_enabled: boolean; deletion_disabled_by_default: boolean } | { unavailable: true };
  critical_warnings: string[]; safety: string;
};
export type OpsStorage = {
  db_size_mb: number; largest_tables: { table: string; rows: number; size_mb: number }[];
  growth_trend: Record<string, unknown>; warnings: { severity: string; detail: string }[]; note: string;
};
export type OffHostReadiness = {
  provisioned: boolean; off_host_class: string; verified: boolean; target_class: string | null;
  encryption_class: string; distinct_host_confirmed: boolean; offhost_ref: string | null;
  off_host_age_seconds: number | null; checksum_verified: boolean; error_class: string | null;
  next_scheduled_at: string | null; blockers: string[]; prerequisite: string | null;
  caveats: string[]; safety: string;
};
export type DrReadiness = {
  dr_status: string; dr_ready: boolean; dr_detail: string | null;
  rpo: { target_human: string; local_age_seconds: number | null; local_met: boolean;
    off_host_age_seconds: number | null; off_host_met: boolean; off_host_available: boolean; reason: string };
  rto: { planning_target_seconds: number | null; local_drill_duration_seconds: number | null;
    local_drill_at: string | null; remote_drill_duration_seconds: number | null;
    remote_drill_at: string | null; evidence_sufficient: boolean; caveat: string };
  local_protection: { classification: string | null; restore_classification: string | null; verified: boolean };
  off_host_protection: { off_host_class: string; provisioned: boolean; verified: boolean };
  remote_restore_drill: { status: string; duration_seconds: number | null; generated_at: string | null };
  limitations: string[]; safety: string;
};
export type BackfillPlan = {
  id: number; source_system: string; connector_domain: string | null; title: string | null; status: string;
  pilot_only: boolean; scope_company: string | null; scope_source: string | null;
  source_date_start: string | null; source_date_end: string | null;
  warehouse_date_start: string | null; warehouse_date_end: string | null;
  required_approver: string | null; approved_at: string | null; block_reason: string | null;
  connector_blocker_waived: boolean; not_executed: boolean; rollback_plan?: string | null;
  stop_conditions?: string | null; validation_rules?: string | null;
};
export type BackfillPilotPackage = {
  available: boolean; status?: string; note?: string; executed?: boolean;
  connector_reliability?: { capture_class: string | null; capturing_live: boolean | null };
  historical_gap?: { warehouse_earliest: string | null; requested_source_start: string | null };
  preconditions?: string[]; source_load_budget?: Record<string, unknown>;
  safeguards?: Record<string, string | null>; recommended_pilot_range?: Record<string, string>;
  no_source_change?: string; links?: Record<string, string>; plan?: BackfillPlan;
};
export type BackfillAssessment = {
  id: number; source_code: string; metric: string | null; label: string;
  earliest_source_date: string | null; earliest_warehouse_date: string | null; backfill_possible: string;
  safe_query_method: string | null; load_impact: string | null; dedupe_key_available: boolean | null;
  expected_volume: string | null; privacy_constraints: string | null; known_limitations: string | null;
  batching_approach: string | null; approval_required: boolean; state: string; evidence: string | null;
};
export type RetentionPolicy = {
  id: number; domain: string; label: string; retention_class: string; retention_days: number | null;
  rationale: string | null; deletion_enabled: boolean; dry_run: boolean; protected: boolean;
  last_dry_run_at: string | null; last_result: string | null;
};
export type RetentionDryRun = {
  domain: string; protected: boolean; retention_class: string; retention_days: number | null;
  cutoff_date: string | null; total_count: number; eligible_count: number; oldest_date: string | null;
  deletion_enabled: boolean; deleted: number; note: string; safety: string;
};
export type PeriodCloseRow = {
  id: number; close_type: string; status: string; title: string | null;
  period_start: string | null; period_end: string | null; content_hash: string | null;
  snapshot_version: string | null; parent_close_id: number | null; void_reason: string | null;
  finalised_at: string | null; created_at: string | null; immutable: boolean;
};
export type PeriodCloseDetail = PeriodCloseRow & {
  payload: Record<string, unknown> | null;
  events: { ts: string | null; event_type: string; actor_email: string | null; note: string | null }[];
  revisions: PeriodCloseRow[];
};
export type BackupReadiness = {
  generated_at: string; last_backup_at: string | null; backup_age_seconds: number | null; backup_ref: string | null;
  location_class: string; size_bytes: number | null; retention_count: number; integrity_status: string;
  live_schema_version: string | null; app_commit: string | null; backup_kind: string; policy_status: string;
  fresh: boolean; caveats: string[]; safety: string;
  last_restore_drill: { status: string; started_at: string | null } | null;
};
export type RestoreDrill = {
  id: number; status: string; backup_ref: string | null; started_at: string | null;
  finished_at: string | null; result_summary: string | null; blocked_reason: string | null;
};
// Connector reliability (Phase 19)
export type ConnectorOp = {
  source: string; label: string; configured: boolean; health_status: string; capture_class: string;
  capturing_live: boolean; provenance: string; latest_run_at: string | null; latest_run_status: string | null;
  latest_success_at: string | null; latest_capture_at: string | null; latest_snapshot_at: string | null;
  latest_snapshot_status: string | null; latest_warehouse_date: string | null;
  successful_run: boolean; successful_snapshot: boolean; successful_warehouse_capture: boolean;
  latest_verified_live_capture: string | null; seeded_or_legacy_data_only: boolean;
  warehouse_dates: number; verified_dates: number; seeded_dates: number;
  failure_class: string | null; failure_note: string | null; impact: string[]; caveats: string[]; recovery: string;
};
export type ConnectorRunRow = {
  started_at: string | null; finished_at: string | null; status: string; run_type: string;
  rows: number; duration_ms: number | null; is_capture: boolean; failure_class: string | null; note: string | null;
};
export type SloConnector = {
  source: string; label: string; criticality: string | null; enabled: boolean; capture_class: string;
  provenance: string | null; state: string; window_hours: number; runs_in_window: number; capture_runs: number;
  failed_runs: number; consecutive_failures: number; freshness_minutes: number | null;
  warn_freshness_minutes: number | null; critical_freshness_minutes: number | null; median_duration_ms: number | null;
  latest_verified_live_capture: string | null; affected_domains: string[]; has_open_incident?: boolean;
  rates: { adequate_sample: boolean; verified_capture_rate_pct?: number; failure_ratio_pct?: number };
};
export type AssuranceResult = {
  summary: Record<string, number>; connectors: SloConnector[];
  open_incidents: RecoveryIncident[]; no_causation: string;
};
export type ConnectorSloProfile = {
  id: number; source_code: string; enabled: boolean; cadence_minutes: number | null;
  warn_freshness_minutes: number | null; critical_freshness_minutes: number | null;
  max_consecutive_failures: number | null; max_failure_ratio: number | null; window_hours: number | null;
  criticality: string | null; affected_domains: string[]; known_limitations: string | null;
  owner: string | null; review_owner: string | null; last_reviewed_at: string | null;
};
export type RecoveryIncident = {
  id: number; source_code: string; status: string; severity: string; title: string; detail: string | null;
  capture_class_at_open: string | null; diagnostic_notes: string | null; operator_step_required: string | null;
  resolution: string | null; resolved_by_verified_capture: boolean | null; linked_action_ref: string | null;
  opened_at: string | null; resolved_at: string | null;
};
// Metric catalogue (Phase 21)
export type MetricEntry = {
  code: string; name: string; domain: string; definition: string; unit: string; financial_class: string;
  value_class: string; sources: string[]; warehouse_kpi: string | null; grain: string; supported_scope: string[];
  unsupported_scope: string[]; formula_version: string; source_limitations: string; owner: string | null;
  prohibited_with: string[]; sources_capturing: boolean | null; seeded_only: boolean; earliest_history: string | null;
  source_states: { source: string; capture_class: string }[];
};
export type MetricContract = {
  code: string; name: string; domain: string; financial_class: string; value_class: string;
  state: string; reasons: string[]; prohibited_with?: string[]; separation?: string;
};
export type MetricProvenance = MetricEntry & {
  contract: MetricContract; coverage: { latest_date: string | null; warehouse_aggregated_at: string | null } | null;
  links: Record<string, string>; safety: string;
};
export type DriftFinding = { kind: string; source: string; severity: string; detail: string };
export type ActionEvent = {
  id: number; ts: string | null; event_type: string; actor_email: string | null;
  from_status: string | null; to_status: string | null; note: string | null;
};

export type CustomerRow = {
  id: string; name: string; email: string | null;
  total_spend: number; ltv_estimate: number; repair_value: number; repair_count: number;
  order_count: number; invoice_count: number; subscription_count: number; support_count: number;
  open_issues: number; services_used: number; last_activity: string | null; first_seen: string | null;
  share_pct?: number | null;
};
export type CustomerSourceRow = {
  source: string; source_id: string; name: string; email: string | null;
  spend: number; txn_count: number; open_count: number; support_count: number; subscription_count: number;
  last_activity: string | null; match_status: string; match_confidence: number;
};
export type CustomerDetail = CustomerRow & { sources: CustomerSourceRow[] };
export type CustomersResponse = { customers: CustomerRow[]; sort: string };
export type CustomerRankings = {
  highest_value: CustomerRow[]; most_active: CustomerRow[]; multi_service: CustomerRow[];
  highest_ltv: CustomerRow[]; profit_note: string;
};
export type ConcentrationResponse = {
  customer_count: number; total_revenue: number; top_n: number; top_share_pct: number | null;
  top1_pct: number | null; top5_pct: number | null; top10_pct: number | null; risk: string;
  top_customers: CustomerRow[];
};
export type CustomerReviewResponse = {
  items: { id: string; source: string; name: string; email: string | null; match_status: string; confidence: number; customer_id: string | null }[];
};

export type ConnectorCatalog = {
  connectors: { code: string; name: string; category: string; transport: string }[];
};

export type RunItem = {
  id: number;
  source: string;
  run_type: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  rows: number;
  error: string | null;
};

function qs(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v) q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : "";
}

export type RangeFilter = { from?: string; to?: string; branch?: string; live?: boolean };

function rangeParams(f: RangeFilter): Record<string, string | undefined> {
  return {
    from: f.from,
    to: f.to,
    branch: f.branch,
    live: f.live ? "true" : undefined,
  };
}

// --- Phase 8: Pacing, Forecasting v2, Board Pack ---
export type PacingFlowRow = {
  key: string; label: string; unit: string; source: string;
  mtd_actual: number; days_elapsed: number; days_in_month: number; days_remaining: number;
  current_daily_run_rate: number; projected_eom: number; has_target: boolean;
  target: number | null; expected_progress: number | null; variance: number | null;
  variance_pct: number | null; pct_achieved: number | null;
  required_daily_run_rate: number | null; projected_miss_surplus: number | null; risk_band: string;
};
export type PacingSlaRow = {
  key: string; label: string; unit: string; source: string;
  actual: number | null; target: number | null; has_target: boolean; lower_better: boolean; risk_band: string;
};
export type PacingMonth = {
  today: string; month_start: string; days_in_month: number; days_elapsed: number;
  days_remaining: number; timezone: string;
};
export type PacingResponse = { generated_at: string; month: PacingMonth; flow: PacingFlowRow[]; sla: PacingSlaRow[] };
export type PacingSummaryRow = {
  name: string; source?: string; unit?: string; mtd_actual: number; projected_eom: number;
  prev_month: number; vs_prev_pct: number | null; band: string;
};
export type PacingSummaryResponse = {
  dim: string; kpi?: string; unit?: string; label?: string;
  month: { today: string; days_elapsed: number; days_in_month: number };
  rows: PacingSummaryRow[]; note: string;
};
export type ForecastV2 = {
  kpi: string; label: string; unit: string; kind: string;
  lookback_days: number; horizon_days: number; company: string | null; branch: string | null;
  points: number; enough_data: boolean; confidence: string;
  projected: number | null; lower: number | null; upper: number | null; slope_per_day: number | null;
  direction: string | null; change_pct: number | null; explanation: string;
};
export type ForecastV2Headline = { horizon_days: number; forecasts: ForecastV2[]; any_data: boolean };
export type ForecastAccuracyKpi = {
  kpi: string; label: string; unit: string; evaluated: number; mape: number | null; bias_pct: number | null;
  recent: { as_of: string; target_end: string; projected: number; actual: number | null; pct_error: number | null }[];
};
export type ForecastAccuracyResponse = { have_data: boolean; kpis: ForecastAccuracyKpi[]; note: string };
export type OutlookResponse = { pacing: PacingResponse; forecasts: ForecastV2Headline };

// --- Phase 9: Trust, Data Quality, Reconciliation, Review ---
export type TrustConnectorRow = {
  source: string; name: string; label: string; score: number; status: string; reason: string;
  freshness: string | null; last_success: string | null; age_seconds: number | null;
  last_run_status: string | null; notes: string[]; affected: string;
};
export type TrustConnectorsResponse = { generated_at: string; connectors: TrustConnectorRow[]; summary: Record<string, number> };
export type TrustKpiRow = {
  kpi: string; label_text: string; unit: string; kind: string; source: string;
  label: string; score: number; reason: string; coverage_days: number; latest_row: string | null;
};
export type TrustKpisResponse = { generated_at: string; kpis: TrustKpiRow[]; summary: Record<string, number> };
export type DataQualityIssue = {
  code: string; source: string | null; severity: string; label: string; reason: string;
  affected_sections: string[]; last_healthy: string | null;
};
export type DataQualityResponse = { generated_at: string; issues: DataQualityIssue[]; counts: Record<string, number> };
export type TrustOverviewResponse = {
  generated_at: string; connectors: Record<string, number>; kpis: Record<string, number>;
  data_quality: Record<string, number>; reconciliation: { counts: Record<string, number>; total: number };
  connector_rows: TrustConnectorRow[]; issue_rows: DataQualityIssue[];
};
export type ReconItem = {
  source_a: string; source_b: string; reference: string | null;
  amount_a: number | null; amount_b: number | null; financial_difference: number | null;
  status: string; confidence: number | null; explanation: string; double_counting_risk: boolean;
};
export type ReconResponse = { generated_at: string; items: ReconItem[]; counts: Record<string, number> };
export type TrustBoardResponse = {
  freshness: { source: string; name: string; label: string; status: string; freshness: string | null }[];
  kpi_trust: { kpi: string; label: string; reason: string }[];
  estimated: DataQualityIssue[]; missing: DataQualityIssue[];
  reconciliation_warnings: ReconItem[]; repair_revenue_note: string;
};
export type ReviewItem = {
  id: number; dedup_key: string; review_type: string; severity: string; confidence: number | null;
  title: string; detail: string | null; source_code: string | null; entity_ref: string | null;
  status: string; owner: string | null; due_date: string | null; notes: string | null;
  created_at: string | null; updated_at: string | null; resolved_at: string | null;
};

// --- Phase 10: Executive Home v2 + Daily Briefing ---
export type HomeCardLine = { label: string; value: number | string | null; unit?: string };
export type HomeCardNote = string | { text: string; link: string };
export type HomeCard = {
  key: string; title: string; link: string; severity: string;
  lines: HomeCardLine[]; notes: HomeCardNote[]; badges: { kind: string; label: string }[];
  count: number | null; empty: boolean;
};
export type HomeResponse = { generated_at: string; view: string; available_views: string[]; cards: HomeCard[] };
export type HomePreferences = { preferences: Record<string, unknown>; default_view: string; available_views: string[] };
export type BriefingItem = { text: string; severity: string; link: string | null };
export type BriefingSection = { key: string; title: string; items: BriefingItem[]; empty: boolean };
export type BriefingResponse = { generated_at: string; since: string; sections: BriefingSection[]; has_data?: boolean };

// --- Phase 11: Notifications delivery reliability, Escalations, Prefs ---
export type DeliveryOverview = {
  by_status: Record<string, number>;
  delivered: number; failed: number; skipped: number; suppressed: number;
  channels: number; subscriptions: number;
};
export type Escalation = {
  id: number; dedup_key: string; escalation_type: string; severity: string; title: string;
  reason: string | null; source_kind: string | null; source_ref: string | null; owner: string | null;
  status: string; escalation_count: number; first_seen_at: string | null; last_escalated_at: string | null;
  notes: string | null; created_at: string | null; updated_at: string | null; resolved_at: string | null;
};
export type EscalationSummary = {
  active: number; unacknowledged: number;
  by_status: Record<string, number>; by_severity: Record<string, number>; by_type: Record<string, number>;
};
export type BriefingDeliveryStatus = {
  schedule: { code: string; enabled: boolean; hour: number; next_run_at: string | null; last_run_at: string | null } | null;
  last_run: { id: number; title: string; trigger: string; generated_at: string | null } | null;
  last_sent_at: string | null; last_failed_at: string | null; last_failed_detail: string | null;
  deliveries: DeliveryItem[];
};
export type NotificationPrefs = {
  preferences: Record<string, unknown>;
  defaults: Record<string, unknown>;
  effective: Record<string, unknown>;
  report_kinds: string[]; severities: string[]; channels: string[];
};


// ---- Phase 12: ecosystem operations ----------------------------------------
export type SourceMeta = { source: string; available: boolean; captured_at: string | null; status: string | null; gaps: string[] };
export type LV = { label: string; value: number };

export type RenewalsResponse = {
  source: SourceMeta;
  headline: { active_subscriptions: number | null; mrr: number | null; open_invoices: number | null; open_invoices_total: number | null; failed_payments_24h: number | null; pending_cancel: number | null; cancelled_in_range: number | null };
  renewals: { workflow_in_use: boolean; cycles_by_state: LV[]; cycles_total: number; overdue_cycles: number; attempts_by_status: LV[]; upcoming_7d: { count: number; expected_total: number | null }; upcoming_30d: { count: number; expected_total: number | null }; prices_available: boolean } | null;
  billing_methods: { mix: LV[]; open_change_requests: number } | null;
  provisioning: { by_state: LV[]; pending: number; service_expiring_7d: number } | null;
  expected_vs_settled: { expected: number | null; settled_payments_ledger: number; settled_manual: number; settled_manual_count: number; settled_total: number; prices_available: boolean } | null;
  dunning: { active: number; oldest_days: number } | null;
  comm_health: { by_state: LV[]; total: number; failed: number; failure_rate_pct: number | null } | null;
  cancel_reasons_recorded: number | null;
};

export type SupportOpsResponse = {
  source: SourceMeta;
  headline: { conversations_open: number | null; sla_breached_now: number | null; first_response_p50_hours: number | null; tickets_open: number | null };
  queue: { states_live: LV[]; archived_live: number; unassigned: number; unassigned_oldest_hours: number; waiting_reply: number; waiting_oldest_hours: number; snoozed: number } | null;
  sla: { warnings_in_range: number; at_risk_now: number; policy_covered: number; policy_total: number; policy_coverage_pct: number | null; policies_enabled: number } | null;
  automation: { rules_enabled: number; rules_total: number; runs_by_status: LV[]; run_errors: number; last_run_at: string | null } | null;
  channels: { by_kind: (LV & { probe_failed: number })[]; probe_failed: number; inbound_errors: number; outbox_failed: number; outbox_stuck: number } | null;
  webchat: { sessions: number; converted: number; identified: number; conversion_pct: number | null; active_now: number } | null;
  agent_load: { name: string; value: number }[];
  c360: { links_by_source: LV[]; links_total: number; customers_total: number } | null;
  voice: {
    calls_in_range: number; missed_in_range: number; missed_unacknowledged: number; followups_overdue: number;
    outbound_in_range?: number; voicemail_in_range?: number; transcribed_in_range?: number; avg_duration_seconds?: number | null;
  } | null;
};

// --- Phase 28: Calls & Voice (IronChat call-log mirror; admin-only) ---
export type TranscriptLine = { speaker: string; text: string; confidence: number | null; source: string | null; at: string | null };
export type VoiceCall = {
  id: string; occurred_at: string | null; direction: string; status: string; missed: boolean; voicemail: boolean;
  from_number: string | null; to_number: string | null; duration_seconds: number | null;
  disposition: string | null; disposition_note: string | null; acknowledged: boolean; callback_logged: boolean;
  assigned_to: string | null; customer_name: string | null; has_recording: boolean;
  transcript_state: string; has_transcript: boolean;
  followup_state: string | null; followup_due_at: string | null; followup_priority: string | null;
};
export type VoiceCallDetail = VoiceCall & { transcript: TranscriptLine[] };
export type VoiceCallsResponse = { items: VoiceCall[]; total: number; view: string };
export type VoiceSummary = {
  available: boolean; calls_total: number; missed_total: number; missed_unacknowledged: number;
  voicemail_total: number; with_transcript: number; followups_open: number; last_sync_at: string | null;
};

export type RepairPipelineResponse = {
  source: SourceMeta;
  headline: { open_tickets: number | null; overdue_tickets: number | null; completed_in_range: number | null; estimated_value_incl: number | null };
  bookings: { by_status: LV[]; total: number; converted: number; no_show: number; conversion_pct: number | null; no_show_pct: number | null; upcoming_24h: number } | null;
  intake: { by_status: LV[]; unconverted_backlog: number } | null;
  quotes: { by_status: LV[]; awaiting_decision: number; expiring_48h: number; decision_p50_hours: number | null } | null;
  stages: { stages: { label: string; value: number; oldest_ticket_days: number }[]; unassigned_by_branch: LV[] } | null;
  parts: { open_orders: number; past_eta: number; avg_lead_days: number | null } | null;
  collection: { ready_now: number; oldest_ticket_days: number; collected_paid_count: number; collected_paid_total: number; methods: LV[] } | null;
  turnaround: { promise_kept: number; promise_total: number; promise_kept_pct: number | null; pickup_lag_avg_days: number | null } | null;
  quality: { warranties_active: number; warranty_claims: number; repeat_repair_devices_90d: number } | null;
  tech_load: { name: string; value: number }[];
  buyback: { by_status: LV[]; paid_count: number; paid_total: number; resale_intents: LV[] } | null;
};

export type CatalogueHealthResponse = {
  source: SourceMeta;
  headline: { products_active: number | null; products_publishable: number | null; failed_imports: number | null; products_new_7d: number | null };
  enrichment: { publishable_total: number; no_description_en: number; no_description_es: number; no_meta_title: number; no_image: number } | null;
  catalog_gaps: { active_total: number; no_ean: number; no_brand: number; no_category: number } | null;
  feed_freshness: { name: string; schedule_enabled: boolean; last_success_at: string | null; hours_since_success: number | null }[];
  chronic_feeds: { name: string; failures_7d: number }[];
  orphaned_jobs: number | null;
  import_quality: { jobs: number; matched_by_ean: number; matched_by_mpn: number; matched_by_sku: number; unresolved: number; errors: number; price_changes: number; stock_changes: number; unchanged_files: number } | null;
  publishing: { ps_flagged: number; odoo_flagged: number; ps_mappings: number; ps_jobs_recent: LV[]; ps_terminal_failures: number; ps_queue_idle_days: number | null; odoo_jobs: LV[] } | null;
  duplicates: { pending_total: number; by_confidence: LV[] } | null;
  audit_activity: { events_24h: number; notable_24h: number } | null;
  resale_queue: { by_status: LV[]; oldest_pending_days: number; published_total: number } | null;
};

export type WorkforceCapacityResponse = {
  source: SourceMeta;
  rota: { shifts_published: number | null; staff_scheduled: number | null; active_staff: number | null; by_company: { name: string; active_staff: number; shifts_published: number }[]; coverage_gaps_next7: number | null; hours: { planned: number; worked: number; coverage_pct: number | null } | null; leave_by_type: LV[]; leave_pending: number | null; leave_pending_oldest_days: number | null; branch_coverage: { name: string; code: string; shifts: number }[] };
  pressure: { repair_open_tickets: number | null; repair_unassigned: number | null; support_waiting_reply: number | null; support_unassigned: number | null; staff_scheduled: number | null; note: string };
};

export type CapabilitySystem = {
  system: string; connector: string; page: string;
  implemented: string[];
  deferred: { item: string; reason: string }[];
  blocked: { item: string; reason: string }[];
};
export type CapabilitiesResponse = {
  systems: CapabilitySystem[];
  summary: { systems: number; implemented: number; deferred: number; blocked: number };
  guarantees: string[];
};


// ---- Phase 13: acceptance / validation / feedback / alert tuning -----------
export type AcceptanceItem = {
  id: number; dedup_key: string; item_type: string; severity: string; title: string;
  target_ref: string | null; source_code: string | null; company: string | null; branch: string | null;
  status: string; owner: string | null; reviewer: string | null; evidence_note: string | null;
  source_comparison: string | null; suggested_improvement: string | null; origin: string;
  created_at: string | null; updated_at: string | null; resolved_at: string | null;
};
export type AcceptanceOverview = {
  total: number; active: number; by_status: Record<string, number>;
  by_type: Record<string, number>; by_severity: Record<string, number>; item_types: string[];
};
export type ValidationFinding = {
  key: string; label: string; source: string; category: string; result: string;
  irondash: unknown; source_value: unknown; reason: string; comparison: string;
};
export type ValidationKpis = { generated_at: string; counts: Record<string, number>; findings: ValidationFinding[] };
export type AlertTuningRow = {
  rule_code: string; enabled: boolean; severity_override: string | null;
  threshold: number | null; grace_minutes: number | null; note: string | null; updated_at: string | null;
};
export type AlertTuningCatalogEntry = { rule_code: string; threshold_default: number; label: string; unit: string; tuning: AlertTuningRow | null };
export type AlertTuningOverview = { tunable: AlertTuningCatalogEntry[]; all_tuning: Record<string, AlertTuningRow>; disabled: string[]; severity_overridden: string[] };

export const dashApi = {
  executive: (f: RangeFilter = {}) =>
    request<ExecutiveResponse>(`/dashboard/executive${qs(rangeParams(f))}`),
  sales: (f: RangeFilter = {}) => request<SalesResponse>(`/dashboard/sales${qs(rangeParams(f))}`),
  insights: () => request<InsightsResponse>("/dashboard/insights"),
  leagues: () => request<LeaguesResponse>("/dashboard/leagues"),
  attention: () => request<AttentionResponse>("/dashboard/attention"),
  targets: () => request<TargetsResponse>("/dashboard/targets"),
  forecast: () => request<ForecastResponse>("/dashboard/forecast"),
  dailySummary: () => request<DailySummaryResponse>("/dashboard/daily-summary"),
  // Historical warehouse
  historyDimensions: () => request<DimensionsResponse>("/dashboard/history/dimensions"),
  historyTrends: (kpi: string, company?: string, branch?: string) =>
    request<KpiTrends>(`/dashboard/history/trends${qs({ kpi, company, branch })}`),
  historySeries: (kpi: string, days = 90, company?: string, branch?: string) =>
    request<SeriesResponse>(`/dashboard/history/series${qs({ kpi, days: String(days), company, branch })}`),
  historyCompare: (kpi: string, dim: "company" | "branch", days = 30) =>
    request<CompareEntitiesResponse>(`/dashboard/history/compare${qs({ kpi, dim, days: String(days) })}`),
  historyForecast: (kpi: string, horizon = 7, company?: string, branch?: string) =>
    request<ForecastKpi>(`/dashboard/history/forecast${qs({ kpi, horizon: String(horizon), company, branch })}`),
  historyLeaders: (board: string, days = 90) =>
    request<LeaderHistoryResponse>(`/dashboard/history/leaders${qs({ board, days: String(days) })}`),
  report: (days = 30) => request<BoardReportResponse>(`/dashboard/report${qs({ days: String(days) })}`),
  warehouseStatus: () => request<WarehouseStatus>("/dashboard/warehouse/status"),
  warehouseRollup: () => request<unknown>("/dashboard/warehouse/rollup", { method: "POST" }),
  warehouseBackfill: (days = 60) => request<unknown>(`/dashboard/warehouse/backfill${qs({ days: String(days) })}`, { method: "POST" }),
  // Reporting & notifications
  digest: () => request<DigestResponse>("/dashboard/digest"),
  reportSchedules: () => request<{ schedules: ReportSchedule[] }>("/dashboard/reports/schedules"),
  reportsRecent: (kind?: string) => request<{ reports: ReportMeta[] }>(`/dashboard/reports/recent${qs({ kind })}`),
  reportDetail: (id: number) => request<ReportDetail>(`/dashboard/reports/${id}`),
  reportPreview: (kind: string) => request<ReportPayload>(`/dashboard/reports/preview${qs({ kind })}`, { method: "POST" }),
  reportRun: (kind: string) => request<{ report: ReportMeta; delivery: Record<string, number> }>(`/dashboard/reports/run${qs({ kind })}`, { method: "POST" }),
  notificationChannels: () => request<{ channels: NotificationChannelItem[] }>("/dashboard/notifications/channels"),
  notificationSubscriptions: () => request<{ subscriptions: SubscriptionItem[] }>("/dashboard/notifications/subscriptions"),
  notificationDeliveries: (limit = 50, status?: string, target?: string) =>
    request<{ deliveries: DeliveryItem[] }>(`/dashboard/notifications/deliveries${qs({ limit: String(limit), status, target })}`),
  notificationsOverview: () => request<DeliveryOverview>("/dashboard/notifications/overview"),
  notificationFailures: (limit = 50) => request<{ deliveries: DeliveryItem[] }>(`/dashboard/notifications/failures${qs({ limit: String(limit) })}`),
  notificationSuppressed: (limit = 50) => request<{ deliveries: DeliveryItem[] }>(`/dashboard/notifications/suppressed${qs({ limit: String(limit) })}`),
  retryDelivery: (id: number) =>
    request<{ capped?: boolean; delivery?: DeliveryItem }>(`/dashboard/notifications/deliveries/${id}/retry`, { method: "POST" }),
  notificationPreferences: () => request<NotificationPrefs>("/dashboard/notifications/preferences"),
  setNotificationPreferences: (body: { daily_briefing_enabled?: boolean; report_subscriptions?: string[]; alert_severity_threshold?: string; channel?: string }) =>
    request<{ preferences: Record<string, unknown> }>("/dashboard/notifications/preferences", { method: "PUT", body: JSON.stringify(body) }),
  targetsReport: () => request<{ targets: TargetReportItem[]; note: string }>("/dashboard/targets/report"),
  reportExportUrl: (id: number, fmt: "html" | "csv") => `/api/dashboard/reports/${id}/export?format=${fmt}`,
  // Customer intelligence
  customers: (sort = "spend", limit = 100, multiService = false) =>
    request<CustomersResponse>(`/dashboard/customers${qs({ sort, limit: String(limit), multi_service: multiService ? "true" : undefined })}`),
  customerDetail: (id: string) => request<CustomerDetail>(`/dashboard/customers/${id}`),
  customerRankings: () => request<CustomerRankings>("/dashboard/customers/rankings"),
  customerConcentration: () => request<ConcentrationResponse>("/dashboard/customers/concentration"),
  customerReview: () => request<CustomerReviewResponse>("/dashboard/customers/review"),
  customerIngest: () => request<unknown>("/dashboard/customers/ingest", { method: "POST" }),
  customerConfirm: (rowId: string) => request<unknown>(`/dashboard/customers/source/${rowId}/confirm`, { method: "POST" }),
  customerReject: (rowId: string) => request<unknown>(`/dashboard/customers/source/${rowId}/reject`, { method: "POST" }),
  customerMerge: (src: string, dst: string) => request<unknown>(`/dashboard/customers/merge${qs({ src, dst })}`, { method: "POST" }),
  sourceHealth: () => request<SourceHealthResponse>("/dashboard/source-health"),
  failures: () => request<FailuresResponse>("/dashboard/failures"),
  trends: (f: RangeFilter = {}) => request<TrendsResponse>(`/dashboard/trends${qs(rangeParams(f))}`),
  leaderboards: (f: RangeFilter = {}) =>
    request<LeaderboardsResponse>(`/dashboard/leaderboards${qs(rangeParams(f))}`),
  recentActivity: () => request<RecentActivityResponse>("/dashboard/recent-activity"),
  source: (code: string, f: RangeFilter = {}) =>
    request<SourceDetail>(`/dashboard/source/${code}${qs(rangeParams(f))}`),
  connectors: () => request<{ connectors: SourceHealthItem[] }>("/connectors"),
  catalog: () => request<ConnectorCatalog>("/connectors/catalog"),
  runs: (source?: string) => request<{ runs: RunItem[] }>(`/connectors/runs${qs({ source })}`),
  syncAll: () => request<unknown>("/connectors/sync", { method: "POST" }),
  syncOne: (code: string) => request<unknown>(`/connectors/${code}/sync`, { method: "POST" }),
  // Executive Decision Centre
  decisionsPriorities: () => request<PrioritiesResponse>("/dashboard/decisions/priorities"),
  decisionsChanges: () => request<WhatChangedResponse>("/dashboard/decisions/changes"),
  decisionsRisks: () => request<RiskRegisterResponse>("/dashboard/decisions/risks"),
  decisionsActions: (status?: string, category?: string) =>
    request<{ actions: ManagementAction[] }>(`/dashboard/decisions/actions${qs({ status, category })}`),
  decisionActionEvents: (id: number) =>
    request<{ events: ActionEvent[] }>(`/dashboard/decisions/actions/${id}/events`),
  createAction: (body: Partial<ManagementAction> & { dedup_key: string; category: string; severity: string; title: string }) =>
    request<{ action: ManagementAction; created: boolean }>("/dashboard/decisions/actions", {
      method: "POST", body: JSON.stringify(body),
    }),
  updateAction: (id: number, body: ActionUpdateBody) =>
    request<{ action: ManagementAction }>(`/dashboard/decisions/actions/${id}`, {
      method: "PATCH", body: JSON.stringify(body),
    }),
  // Accountability workflow (Phase 14)
  accountabilityOverview: () => request<AccountabilityOverview>("/dashboard/accountability/overview"),
  accountabilityMetrics: (days?: number) =>
    request<AccountabilityMetrics>(`/dashboard/accountability/metrics${qs({ days: days ? String(days) : undefined })}`),
  accountabilityReopened: () => request<{ actions: ManagementAction[] }>("/dashboard/accountability/reopened"),
  accountabilityTemplates: () => request<{ templates: ActionTemplate[] }>("/dashboard/accountability/templates"),
  accountabilityActions: (f: {
    status?: string; category?: string; priority?: string; owner?: string; company?: string;
    branch?: string; source_code?: string; link_type?: string; from?: string; to?: string;
  }) => request<{ actions: ManagementAction[] }>(`/dashboard/accountability/actions${qs({ ...f })}`),
  accountabilityAction: (id: number) =>
    request<{ action: ManagementAction; links: ActionLink[]; events: ActionEvent[] }>(
      `/dashboard/accountability/actions/${id}`),
  accountabilityActionLinks: (id: number) =>
    request<{ links: ActionLink[] }>(`/dashboard/accountability/actions/${id}/links`),
  addActionLink: (id: number, body: { link_type: string; link_ref: string; label?: string }) =>
    request<{ link: ActionLink; created: boolean }>(`/dashboard/accountability/actions/${id}/links`, {
      method: "POST", body: JSON.stringify(body),
    }),
  deleteActionLink: (id: number, linkId: number) =>
    request<{ deleted: boolean }>(`/dashboard/accountability/actions/${id}/links/${linkId}`, { method: "DELETE" }),
  reviewAction: (id: number, body: { concern_resolved: boolean; note?: string }) =>
    request<{ action: ManagementAction }>(`/dashboard/accountability/actions/${id}/review`, {
      method: "POST", body: JSON.stringify(body),
    }),
  createActionFromSource: (body: {
    source_type: string; source_ref: string; dedup_key: string; category: string; severity: string;
    title: string; priority?: string; detail?: string; company?: string; branch?: string;
    owner?: string; due_date?: string; planned_action?: string; success_criteria?: string; link_label?: string;
  }) => request<{ action: ManagementAction; created: boolean; suppressed_reason: string | null }>(
    "/dashboard/accountability/actions/from-source", { method: "POST", body: JSON.stringify(body) }),
  acceptanceLinkedActions: (itemId: number) =>
    request<{ actions: ManagementAction[] }>(`/dashboard/acceptance/items/${itemId}/linked-actions`),
  // Decision Records + Outcomes (Phase 16)
  decisionsSummary: () => request<DecisionSummary>("/dashboard/decisions/summary"),
  decisionRecords: (f: { status?: string; category?: string; owner?: string } = {}) =>
    request<{ decisions: DecisionRecord[] }>(`/dashboard/decisions${qs({ ...f })}`),
  decisionRecord: (id: number) => request<DecisionFull>(`/dashboard/decisions/${id}`),
  decisionEvents: (id: number) =>
    request<{ events: { id: number; ts: string | null; event_type: string; actor_email: string | null; from_status: string | null; to_status: string | null; note: string | null }[] }>(
      `/dashboard/decisions/${id}/events`),
  decisionOutcomes: (id: number) => request<{ outcomes: OutcomeComparison[] }>(`/dashboard/decisions/${id}/outcomes`),
  createDecision: (body: Record<string, unknown>) =>
    request<DecisionFull>("/dashboard/decisions", { method: "POST", body: JSON.stringify(body) }),
  updateDecision: (id: number, body: Record<string, unknown>) =>
    request<DecisionFull>(`/dashboard/decisions/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  decisionApprove: (id: number, note?: string) =>
    request<{ decision: DecisionRecord }>(`/dashboard/decisions/${id}/approve`, { method: "POST", body: JSON.stringify({ note }) }),
  decisionReview: (id: number, review_note: string) =>
    request<{ decision: DecisionRecord }>(`/dashboard/decisions/${id}/review`, { method: "POST", body: JSON.stringify({ review_note }) }),
  decisionCancel: (id: number, reason: string) =>
    request<{ decision: DecisionRecord }>(`/dashboard/decisions/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),
  decisionSupersede: (id: number, replaced_by?: number) =>
    request<{ decision: DecisionRecord }>(`/dashboard/decisions/${id}/supersede`, { method: "POST", body: JSON.stringify({ replaced_by }) }),
  decisionTransition: (id: number, statusTo: string) =>
    request<{ decision: DecisionRecord }>(`/dashboard/decisions/${id}/transition`, { method: "POST", body: JSON.stringify({ status: statusTo }) }),
  decisionLink: (id: number, body: { link_type: string; link_ref: string; label?: string }) =>
    request<{ link: unknown; created: boolean }>(`/dashboard/decisions/${id}/link`, { method: "POST", body: JSON.stringify(body) }),
  scenarioQuality: (f: { scope_status?: string; maturity?: string } = {}) =>
    request<{ scenarios: ScenarioQualityRow[] }>(`/dashboard/scenarios/quality${qs({ ...f })}`),
  scenarioOutcomeCompare: (id: number) => request<OutcomeComparison>(`/dashboard/scenarios/${id}/outcomes`),
  // Decision Portfolio, Cadence & Effectiveness (Phase 17)
  portfolioSummary: (f: Record<string, string | undefined> = {}) =>
    request<PortfolioSummary>(`/dashboard/decision-portfolio/summary${qs({ ...f })}`),
  portfolioAgenda: (f: Record<string, string | undefined> = {}) =>
    request<AgendaResult>(`/dashboard/decision-portfolio/agenda${qs({ ...f })}`),
  portfolioEffectiveness: () => request<EffectivenessResult>("/dashboard/decision-portfolio/effectiveness"),
  portfolioReopened: () => request<{ items: DecisionRecord[]; count: number; note: string }>("/dashboard/decision-portfolio/reopened"),
  cadences: () => request<{ cadences: Cadence[] }>("/dashboard/decision-cadences"),
  createCadence: (body: Record<string, unknown>) =>
    request<{ cadence: Cadence }>("/dashboard/decision-cadences", { method: "POST", body: JSON.stringify(body) }),
  updateCadence: (id: number, body: Record<string, unknown>) =>
    request<{ cadence: Cadence }>(`/dashboard/decision-cadences/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  cadenceEvents: (id: number) =>
    request<{ events: { id: number; ts: string | null; event_type: string; actor_email: string | null; note: string | null }[] }>(`/dashboard/decision-cadences/${id}/events`),
  cadencePreview: (id: number) => request<AgendaResult>(`/dashboard/decision-cadences/${id}/preview`, { method: "POST", body: "{}" }),
  cadenceRun: (id: number, note?: string) =>
    request<{ run_id: number; duplicate: boolean; period_key: string }>(`/dashboard/decision-cadences/${id}/run`, { method: "POST", body: JSON.stringify(note ? { note } : {}) }),
  cadenceNote: (id: number, note: string) =>
    request<{ status: string }>(`/dashboard/decision-cadences/${id}/note`, { method: "POST", body: JSON.stringify({ note }) }),
  decisionGovernance: (id: number) => request<GovernanceResult>(`/dashboard/decisions/${id}/governance`),
  decisionAcceptedRisks: (id: number) => request<{ risks: AcceptedRisk[] }>(`/dashboard/decisions/${id}/accepted-risks`),
  changeReviewDate: (id: number, new_date: string, reason: string) =>
    request<{ decision: DecisionRecord }>(`/dashboard/decisions/${id}/change-review-date`, { method: "POST", body: JSON.stringify({ new_date, reason }) }),
  acceptRisk: (id: number, body: Record<string, unknown>) =>
    request<{ risk: AcceptedRisk }>(`/dashboard/decisions/${id}/accept-risk`, { method: "POST", body: JSON.stringify(body) }),
  reconfirmRisk: (riskId: number, body: Record<string, unknown>) =>
    request<{ risk: AcceptedRisk }>(`/dashboard/decision-risks/${riskId}/reconfirm`, { method: "POST", body: JSON.stringify(body) }),
  withdrawRisk: (riskId: number, reason: string) =>
    request<{ risk: AcceptedRisk }>(`/dashboard/decision-risks/${riskId}/withdraw`, { method: "POST", body: JSON.stringify({ reason }) }),
  // Historical durability (Phase 18)
  historyCoverage: () => request<{ sources: CoverageSource[]; global_earliest: string | null }>("/dashboard/history/coverage"),
  historyCoverageSummary: () => request<CoverageSummary>("/dashboard/history/coverage/summary"),
  backfillReadiness: () => request<{ assessments: BackfillAssessment[]; note: string }>("/dashboard/history/backfill-readiness"),
  backfillPlans: () => request<{ plans: BackfillPlan[]; note: string }>("/dashboard/backfill/plans"),
  backfillPlan: (id: number) => request<BackfillPlan & { events: unknown[] }>(`/dashboard/backfill/plans/${id}`),
  createBackfillPlan: (body: Record<string, unknown>) =>
    request<{ plan: BackfillPlan }>("/dashboard/backfill/plans", { method: "POST", body: JSON.stringify(body) }),
  updateBackfillPlan: (id: number, body: Record<string, unknown>) =>
    request<{ plan: BackfillPlan }>(`/dashboard/backfill/plans/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  backfillPlanAction: (id: number, action: string, body: Record<string, unknown> = {}) =>
    request<{ plan: BackfillPlan }>(`/dashboard/backfill/plans/${id}/${action}`, { method: "POST", body: JSON.stringify(body) }),
  backfillPilotPackage: (id: number) => request<BackfillPilotPackage>(`/dashboard/backfill/plans/${id}/pilot-package`),
  // Operational readiness (Phase 23)
  opsReadiness: () => request<OpsReadiness>("/dashboard/operations/readiness"),
  opsStorage: () => request<OpsStorage>("/dashboard/operations/storage"),
  opsBackups: () => request<{ backup: BackupReadiness; automation: OpsBackupAutomation }>("/dashboard/operations/backups"),
  // Off-host replication + DR readiness (Phase 25)
  opsOffhost: () => request<OffHostReadiness>("/dashboard/operations/offhost"),
  opsDrReadiness: () => request<DrReadiness>("/dashboard/operations/dr-readiness"),
  updateBackfill: (source: string, body: Record<string, unknown>) =>
    request<{ assessment: BackfillAssessment }>(`/dashboard/history/backfill-readiness/${source}`, { method: "PATCH", body: JSON.stringify(body) }),
  retention: () => request<{ policies: RetentionPolicy[]; note: string; any_deletion_enabled: boolean }>("/dashboard/history/retention"),
  retentionVolume: () => request<{ domains: { domain: string; label: string; rows: number }[] }>("/dashboard/history/retention/volume-trend"),
  updateRetention: (domain: string, body: Record<string, unknown>) =>
    request<{ policy: RetentionPolicy }>(`/dashboard/history/retention/${domain}`, { method: "PATCH", body: JSON.stringify(body) }),
  retentionDryRun: (domain: string) =>
    request<RetentionDryRun>(`/dashboard/history/retention/${domain}/dry-run`, { method: "POST", body: "{}" }),
  periodCloses: (f: { close_type?: string; status?: string } = {}) =>
    request<{ closes: PeriodCloseRow[] }>(`/dashboard/history/period-closes${qs({ ...f })}`),
  periodClose: (id: number) => request<PeriodCloseDetail>(`/dashboard/history/period-closes/${id}`),
  periodClosePreview: (body: Record<string, unknown>) =>
    request<{ preview: boolean; content_hash: string; period_start: string; period_end: string; payload: unknown; disclaimer: string }>(
      "/dashboard/history/period-closes/preview", { method: "POST", body: JSON.stringify(body) }),
  createPeriodClose: (body: Record<string, unknown>) =>
    request<{ close: PeriodCloseRow }>("/dashboard/history/period-closes", { method: "POST", body: JSON.stringify(body) }),
  finalisePeriodClose: (id: number) =>
    request<{ close: PeriodCloseRow; created: boolean }>(`/dashboard/history/period-closes/${id}/finalise`, { method: "POST", body: "{}" }),
  voidPeriodClose: (id: number, reason: string) =>
    request<{ close: PeriodCloseRow }>(`/dashboard/history/period-closes/${id}/void`, { method: "POST", body: JSON.stringify({ reason }) }),
  supersedePeriodClose: (id: number, note: string) =>
    request<{ revision: PeriodCloseRow }>(`/dashboard/history/period-closes/${id}/supersede`, { method: "POST", body: JSON.stringify({ note }) }),
  backups: () => request<BackupReadiness>("/dashboard/history/backups"),
  restoreReadiness: () => request<{ backup: BackupReadiness; drills: RestoreDrill[] }>("/dashboard/history/restore-readiness"),
  restoreDrillPreview: () => request<{ plan: string[]; blocked_reason: string; can_run_from_app: boolean; safety: string }>(
    "/dashboard/history/restore-drill/preview", { method: "POST", body: "{}" }),
  runRestoreDrill: () => request<{ status: string; run_id: number; blocked_reason: string }>(
    "/dashboard/history/restore-drill/run", { method: "POST", body: "{}" }),
  // Connector reliability (Phase 19)
  connectorOperations: () => request<{ summary: Record<string, unknown>; connectors: ConnectorOp[] }>("/dashboard/connectors/operations"),
  connectorCaptureHistory: (code: string) =>
    request<{ source: string; runs: ConnectorRunRow[] }>(`/dashboard/connectors/${code}/capture-history`),
  runConnector: (code: string) =>
    request<{ source: string; status: string; is_capture: boolean; rows: number; note: string }>(
      `/dashboard/connectors/${code}/run`, { method: "POST", body: "{}" }),
  // Connector SLOs + Data Capture Assurance + recovery (Phase 20)
  connectorAssurance: () => request<AssuranceResult>("/dashboard/connectors/assurance"),
  connectorSloList: () => request<{ slos: ConnectorSloProfile[] }>("/dashboard/connectors/slo"),
  updateConnectorSlo: (code: string, body: Record<string, unknown>) =>
    request<{ slo: ConnectorSloProfile }>(`/dashboard/connectors/slo/${code}`, { method: "PATCH", body: JSON.stringify(body) }),
  openIncident: (body: Record<string, unknown>) =>
    request<{ incident: RecoveryIncident }>("/dashboard/connectors/incidents", { method: "POST", body: JSON.stringify(body) }),
  incidentAck: (id: number) => request<{ incident: RecoveryIncident }>(`/dashboard/connectors/incidents/${id}/acknowledge`, { method: "POST", body: "{}" }),
  incidentNote: (id: number, note: string, kind = "diagnostic") =>
    request<{ incident: RecoveryIncident }>(`/dashboard/connectors/incidents/${id}/note`, { method: "POST", body: JSON.stringify({ note, kind }) }),
  incidentResolve: (id: number, body: { resolution?: string; by_verified_capture: boolean }) =>
    request<{ incident: RecoveryIncident }>(`/dashboard/connectors/incidents/${id}/resolve`, { method: "POST", body: JSON.stringify(body) }),
  incidentCancel: (id: number, reason: string) =>
    request<{ incident: RecoveryIncident }>(`/dashboard/connectors/incidents/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),
  // Metric catalogue + contracts + drift (Phase 21)
  metricCatalogue: () => request<{ metrics: MetricEntry[]; note: string }>("/dashboard/metrics/catalogue"),
  metricContracts: () => request<{ summary: Record<string, number>; contracts: MetricContract[]; note: string }>("/dashboard/metrics/contracts"),
  metricProvenance: (code: string) => request<MetricProvenance>(`/dashboard/metrics/${code}/provenance`),
  metricDrift: () => request<{ findings: DriftFinding[]; count: number; note: string }>("/dashboard/metrics/drift"),
  // Scenario planning (Phase 15)
  scenarios: (f: { status?: string; scenario_type?: string; owner?: string } = {}) =>
    request<{ scenarios: Scenario[] }>(`/dashboard/scenarios${qs({ ...f })}`),
  scenariosSummary: () => request<ScenarioSummary>("/dashboard/scenarios/summary"),
  scenariosTemplates: () => request<{ templates: ScenarioTemplate[] }>("/dashboard/scenarios/templates"),
  scenariosCompare: (ids: number[]) =>
    request<ScenarioCompare>(`/dashboard/scenarios/compare${qs({ ids: ids.join(",") })}`),
  scenario: (id: number) => request<ScenarioFull>(`/dashboard/scenarios/${id}`),
  scenarioOutputs: (id: number) => request<ScenarioOutputs>(`/dashboard/scenarios/${id}/outputs`),
  scenarioEvents: (id: number) =>
    request<{ events: { id: number; ts: string | null; event_type: string; actor_email: string | null; note: string | null }[] }>(
      `/dashboard/scenarios/${id}/events`),
  createScenario: (body: {
    title: string; scenario_type: string; description?: string; horizon?: string;
    company?: string; branch?: string; source_code?: string; owner?: string;
  }) => request<ScenarioFull>("/dashboard/scenarios", { method: "POST", body: JSON.stringify(body) }),
  updateScenario: (id: number, body: {
    title?: string; description?: string; status?: string; owner?: string; refresh_baseline?: boolean;
    assumptions?: { key: string; proposed_value?: number; bool_value?: boolean }[];
  }) => request<ScenarioFull>(`/dashboard/scenarios/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  duplicateScenario: (id: number) =>
    request<ScenarioFull>(`/dashboard/scenarios/${id}/duplicate`, { method: "POST" }),
  archiveScenario: (id: number) =>
    request<{ scenario: Scenario }>(`/dashboard/scenarios/${id}/archive`, { method: "POST" }),
  linkScenario: (id: number, body: { link_type: string; link_ref: string; label?: string }) =>
    request<{ link: ScenarioLink; created: boolean }>(`/dashboard/scenarios/${id}/link-action`, {
      method: "POST", body: JSON.stringify(body),
    }),
  // Reporting Centre admin (mutations; reads above)
  updateSchedule: (code: string, body: { enabled?: boolean; cadence?: string; hour?: number; weekday?: number; monthday?: number }) =>
    request<{ schedule: ReportSchedule }>(`/dashboard/reports/schedules/${code}`, {
      method: "PATCH", body: JSON.stringify(body),
    }),
  createChannel: (body: { name: string; type: string; config?: Record<string, unknown>; enabled?: boolean }) =>
    request<{ channel: NotificationChannelItem }>("/dashboard/notifications/channels", {
      method: "POST", body: JSON.stringify(body),
    }),
  updateChannel: (id: number, body: { name?: string; config?: Record<string, unknown>; enabled?: boolean }) =>
    request<{ channel: NotificationChannelItem }>(`/dashboard/notifications/channels/${id}`, {
      method: "PATCH", body: JSON.stringify(body),
    }),
  deleteChannel: (id: number) =>
    request<{ deleted: boolean }>(`/dashboard/notifications/channels/${id}`, { method: "DELETE" }),
  createSubscription: (body: { channel_id: number; target: string; report_kind?: string | null; min_severity?: string | null; mode?: string; enabled?: boolean }) =>
    request<{ subscription: SubscriptionItem }>("/dashboard/notifications/subscriptions", {
      method: "POST", body: JSON.stringify(body),
    }),
  updateSubscription: (id: number, body: { report_kind?: string | null; min_severity?: string | null; mode?: string; enabled?: boolean }) =>
    request<{ subscription: SubscriptionItem }>(`/dashboard/notifications/subscriptions/${id}`, {
      method: "PATCH", body: JSON.stringify(body),
    }),
  deleteSubscription: (id: number) =>
    request<{ deleted: boolean }>(`/dashboard/notifications/subscriptions/${id}`, { method: "DELETE" }),
  // Phase 8: pacing / forecasting v2 / board pack
  pacing: () => request<PacingResponse>("/dashboard/pacing"),
  pacingSummary: (dim: string) => request<PacingSummaryResponse>(`/dashboard/pacing/summary${qs({ dim })}`),
  forecastsV2: (kpi?: string, horizon = 7) =>
    request<ForecastV2Headline | ForecastV2>(`/dashboard/forecasts/v2${qs({ kpi, horizon: String(horizon) })}`),
  forecastAccuracy: () => request<ForecastAccuracyResponse>("/dashboard/forecasts/accuracy"),
  outlook: () => request<OutlookResponse>("/dashboard/outlook"),
  boardPackPreview: () => request<ReportPayload>("/dashboard/board-pack/preview"),
  boardPackRun: () => request<{ report: ReportMeta; delivery: Record<string, number> }>("/dashboard/board-pack/run", { method: "POST" }),
  // Phase 9: trust / data quality / reconciliation / review
  trustOverview: () => request<TrustOverviewResponse>("/dashboard/trust/overview"),
  trustConnectors: () => request<TrustConnectorsResponse>("/dashboard/trust/connectors"),
  trustKpis: () => request<TrustKpisResponse>("/dashboard/trust/kpis"),
  trustDataQuality: () => request<DataQualityResponse>("/dashboard/trust/data-quality"),
  trustBoardPack: () => request<TrustBoardResponse>("/dashboard/trust/board-pack"),
  reconciliation: () => request<ReconResponse>("/dashboard/reconciliation"),
  reconciliationSummary: () => request<{ counts: Record<string, number>; total: number }>("/dashboard/reconciliation/summary"),
  reviewItems: (status?: string, reviewType?: string) =>
    request<{ items: ReviewItem[] }>(`/dashboard/trust/review-items${qs({ status, review_type: reviewType })}`),
  reviewEvents: (id: number) => request<{ events: ActionEvent[] }>(`/dashboard/trust/review-items/${id}/events`),
  createReview: (body: { dedup_key: string; review_type: string; severity: string; title: string; detail?: string; source_code?: string | null; entity_ref?: string | null; confidence?: number | null }) =>
    request<{ item: ReviewItem; created: boolean }>("/dashboard/trust/review-items", { method: "POST", body: JSON.stringify(body) }),
  updateReview: (id: number, body: { status?: string; owner?: string; due_date?: string | null; clear_due?: boolean; note?: string }) =>
    request<{ item: ReviewItem }>(`/dashboard/trust/review-items/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  // Phase 10: executive home + daily briefing
  home: (view?: string) => request<HomeResponse>(`/dashboard/home${qs({ view })}`),
  homePreferences: () => request<HomePreferences>("/dashboard/home/preferences"),
  setHomePreferences: (body: { preferred_view?: string; default_branch?: string; default_company?: string; collapsed?: string[] }) =>
    request<{ preferences: Record<string, unknown> }>("/dashboard/home/preferences", { method: "PUT", body: JSON.stringify(body) }),
  briefingDaily: () => request<BriefingResponse>("/dashboard/briefing/daily"),
  briefingPreview: () => request<ReportPayload>("/dashboard/briefing/preview"),
  briefingRun: () => request<{ report: ReportMeta; delivery: Record<string, number> }>("/dashboard/briefing/run", { method: "POST" }),
  // Phase 11: escalations + briefing delivery status + send
  escalations: (status?: string, escalationType?: string) =>
    request<{ escalations: Escalation[] }>(`/dashboard/escalations${qs({ status, escalation_type: escalationType })}`),
  escalationsSummary: () => request<EscalationSummary>("/dashboard/escalations/summary"),
  escalationEvents: (id: number) => request<{ events: ActionEvent[] }>(`/dashboard/escalations/${id}/events`),
  updateEscalation: (id: number, body: { status?: string; owner?: string; note?: string }) =>
    request<{ escalation: Escalation }>(`/dashboard/escalations/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  briefingDeliveryStatus: () => request<BriefingDeliveryStatus>("/dashboard/briefing/delivery-status"),
  briefingSend: () =>
    request<{ suppressed: boolean; reason?: string; report?: ReportMeta; delivery?: Record<string, number> }>("/dashboard/briefing/send", { method: "POST" }),
  // Phase 12: ecosystem operations
  paymentsRenewals: () => request<RenewalsResponse>("/dashboard/payments/renewals"),
  supportOperations: () => request<SupportOpsResponse>("/dashboard/support/operations"),
  // Calls & Voice (admin-only)
  voiceSummary: () => request<VoiceSummary>("/dashboard/voice/summary"),
  voiceCalls: (view = "all", q?: string, limit = 100, offset = 0) =>
    request<VoiceCallsResponse>(`/dashboard/voice/calls${qs({ view, q, limit: String(limit), offset: offset ? String(offset) : undefined })}`),
  voiceCallDetail: (id: string) => request<VoiceCallDetail>(`/dashboard/voice/calls/${id}`),
  repairPipeline: () => request<RepairPipelineResponse>("/dashboard/repairs/pipeline"),
  catalogueHealth: () => request<CatalogueHealthResponse>("/dashboard/pim/catalogue-health"),
  workforceCapacity: () => request<WorkforceCapacityResponse>("/dashboard/workforce/capacity"),
  ecosystemCapabilities: () => request<CapabilitiesResponse>("/dashboard/ecosystem/capabilities"),
  // Phase 13: acceptance / validation / feedback / tuning
  acceptanceOverview: () => request<AcceptanceOverview>("/dashboard/acceptance/overview"),
  acceptanceItems: (status?: string, item_type?: string, source_code?: string) =>
    request<{ items: AcceptanceItem[] }>(`/dashboard/acceptance/items${qs({ status, item_type, source_code })}`),
  acceptanceItemEvents: (id: number) => request<{ events: ActionEvent[] }>(`/dashboard/acceptance/items/${id}/events`),
  createAcceptanceItem: (body: { dedup_key: string; item_type: string; severity?: string; title: string } & Partial<AcceptanceItem>) =>
    request<{ item: AcceptanceItem; created: boolean }>("/dashboard/acceptance/items", { method: "POST", body: JSON.stringify(body) }),
  updateAcceptanceItem: (id: number, body: { status?: string; owner?: string; reviewer?: string; evidence_note?: string; suggested_improvement?: string; note?: string }) =>
    request<{ item: AcceptanceItem }>(`/dashboard/acceptance/items/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  validationKpis: () => request<ValidationKpis>("/dashboard/validation/kpis"),
  validationAlerts: () => request<{ generated_at: string; open_count: number; rules_firing: number; possibly_noisy_rules: string[]; findings: { rule_code: string; source: string | null; severity: string; title: string; age_days: number; per_rule_count: number; actionable: boolean; signals: string[]; verdict: string }[] }>("/dashboard/validation/alerts"),
  validationNotifications: () => request<{ generated_at: string; total: number; by_status: Record<string, number>; retry_cap: number; deliveries_over_cap: number; dedupe_violations: number; verdict: string; reason: string }>("/dashboard/validation/notifications"),
  validationEscalations: () => request<{ generated_at: string; total: number; active: number; by_status: Record<string, number>; active_dedupe_violations: number; verdict: string; reason: string }>("/dashboard/validation/escalations"),
  validationDeepLinks: () => request<{ generated_at: string; checked: number; broken: string[]; verdict: string; reason: string }>("/dashboard/validation/deep-links"),
  seedValidationItems: () => request<{ seeded: { created: number; skipped: number }; counts: Record<string, number> }>("/dashboard/validation/kpis/seed-items", { method: "POST" }),
  feedbackMine: (target_type?: string, target_ref?: string) =>
    request<{ feedback: { target_type: string; target_ref: string; verdict: string; note: string | null; created_at: string | null }[] }>(`/dashboard/feedback${qs({ target_type, target_ref })}`),
  submitFeedback: (body: { target_type: string; target_ref: string; verdict: string; note?: string }) =>
    request<{ target_type: string; target_ref: string; verdict: string }>("/dashboard/feedback", { method: "POST", body: JSON.stringify(body) }),
  feedbackSummary: () => request<{ targets: { target_type: string; target_ref: string; total: number; by_verdict: Record<string, number> }[]; totals: Record<string, number>; total: number }>("/dashboard/feedback/summary"),
  alertTuningOverview: () => request<AlertTuningOverview>("/dashboard/alert-tuning/overview"),
  alertTuningHistory: (rule_code?: string) => request<{ history: { id: number; rule_code: string; ts: string | null; actor_email: string | null; field: string; prior_value: string | null; new_value: string | null; reason: string | null }[] }>(`/dashboard/alert-tuning/history${qs({ rule_code })}`),
  setAlertTuning: (rule_code: string, body: { enabled?: boolean; severity_override?: string; clear_severity?: boolean; threshold?: number; clear_threshold?: boolean; grace_minutes?: number; clear_grace?: boolean; reason?: string }) =>
    request<{ tuning: AlertTuningRow }>(`/dashboard/alert-tuning/${rule_code}`, { method: "PUT", body: JSON.stringify(body) }),
  resetAlertTuning: (rule_code: string) => request<{ reset: boolean; rule_code: string }>(`/dashboard/alert-tuning/${rule_code}`, { method: "DELETE" }),
};
